import {
    Lead,
    LeadPipelineStage,
    LeadSource,
} from '../../../models/Outreach/types'
import { UnitConfigAreaCounts } from '../../../models/Config/types'
import {
    canResendScreeningInvitation,
    canSendLeasePackage,
    canSendScreeningLink,
    canStartScreening,
} from '../../../utils/screeningStatus'
import { PipelineTab, resolveLeadTabId } from './pipelineTabs'
import { maxIsoDate } from './relativeTime'
import { PipelineRecord, PipelineSource, PipelineStage } from './types'

function mapLeadSource(source: LeadSource): PipelineSource {
    switch (source) {
        case LeadSource.EMAIL:
            return 'campaign'
        case LeadSource.PHONE:
            return 'call'
        case LeadSource.PROPERTY:
            return 'walk_in'
        case LeadSource.WEBSITE:
        case LeadSource.LEADPIPE:
            return 'website'
        default:
            return 'campaign'
    }
}

function mapPipelineStage(stage: LeadPipelineStage): PipelineStage {
    if (stage === 'app_signed_pending_review') {
        return 'app_sent'
    }
    if (
        stage === 'leads' ||
        stage === 'tours' ||
        stage === 'app_sent' ||
        stage === 'screening' ||
        stage === 'lease_sent' ||
        stage === 'lease_signed' ||
        stage === 'guarantor' ||
        stage === 'future_resident'
    ) {
        return stage
    }
    return 'leads'
}

function prospectEmail(lead: Lead): string {
    const nested = lead.campaign_prospect?.prospect?.email
    const nestedValue =
        typeof nested === 'string' ? nested : nested?.value ?? ''
    return (
        lead.identity?.email || lead.prospect_detail?.email || nestedValue || ''
    )
}

function prospectPhone(lead: Lead): string {
    return lead.campaign_prospect?.prospect?.phone?.value ?? ''
}

function prospectName(lead: Lead): string {
    const nested = lead.campaign_prospect?.prospect?.name
    return (
        lead.identity?.name || lead.prospect_detail?.name || nested || 'Unknown'
    )
}

export function mapLeadToPipelineRecord(
    lead: Lead,
    tabs?: PipelineTab[],
): PipelineRecord {
    const unitConfig = lead.preferred_unit_config as UnitConfigAreaCounts | null
    const signing = lead.application_signing
    const leasePackage = lead.lease_package
    const tabId = tabs ? resolveLeadTabId(lead, tabs) : null
    const tabLabel = tabId
        ? tabs?.find((tab) => tab.id === tabId)?.label
        : undefined

    return {
        id: `lead-${lead.id}`,
        leadId: lead.id,
        name: prospectName(lead),
        email: prospectEmail(lead),
        phone: prospectPhone(lead),
        stage: mapPipelineStage(lead.pipeline_stage),
        tabId: tabId ?? undefined,
        tabLabel,
        owner: 'human',
        automation: 'active',
        source: mapLeadSource(lead.lead_source),
        unit_interest: unitConfig?.name ?? undefined,
        preferred_unit_config_id: unitConfig?.id ?? undefined,
        preferred_premium_ids: (lead.preferred_premiums ?? []).map(
            (premium) => premium.id,
        ),
        beds: unitConfig?.bed_count ?? undefined,
        budget: lead.preferred_rent_cost ?? undefined,
        intent_score: undefined,
        isRealLead: true,
        pending_application_review:
            lead.pipeline_stage === 'app_signed_pending_review' ||
            Boolean(signing?.pending_review),
        app_sent_date: signing?.sent_at ?? undefined,
        app_progress: signing?.signing_progress ?? undefined,
        lease_sent_date: leasePackage?.sent_at ?? undefined,
        lease_progress: leasePackage?.progress ?? undefined,
        lease_signed_date: leasePackage?.signed_at ?? undefined,
        missing_signatures: leasePackage?.missing_signatures ?? undefined,
        lease_package_status: leasePackage?.status ?? undefined,
        lease_awaiting_property: leasePackage?.awaiting_property ?? undefined,
        application_status: lead.application_status ?? null,
        application_fee_cents:
            lead.application_fee_cents ??
            signing?.application_fee_cents ??
            (lead.application_fee_payment
                ? Math.round(Number(lead.application_fee_payment.amount) * 100)
                : undefined),
        application_fee_paid_at:
            lead.application_fee_payment?.paid_at ?? undefined,
        application_fee_status: lead.application_fee_payment?.status ?? null,
        qualifying_method: lead.qualifying_method ?? null,
        workbook_sections_complete: lead.workbook_sections_complete ?? null,
        workbook_sections_total: lead.workbook_sections_total ?? null,
        workbook_last_activity_at: lead.workbook_last_activity_at ?? null,
        has_submission_pdf: Boolean(lead.has_submission_pdf),
        account_status: lead.account_status ?? 'none',
        invitation_sent_at: lead.invitation_sent_at ?? null,
        account_activated_at: lead.account_activated_at ?? null,
        user_id: lead.user_id ?? null,
        next_action: lead.waiting_on?.label
            ? {
                  waiting_on: lead.waiting_on.waiting_on,
                  label: lead.waiting_on.label,
                  waiting_since: lead.waiting_on.waiting_since ?? null,
              }
            : null,
        last_activity_at: maxIsoDate(
            lead.invitation_sent_at,
            lead.account_activated_at,
            signing?.sent_at,
            lead.application_fee_payment?.paid_at,
            lead.workbook_last_activity_at,
            lead.campaign_prospect?.last_step_sent_at,
            lead.campaign_prospect?.replied_at,
            lead.current_tour?.starts_at,
            lead.current_tour?.completed_at,
            lead.created_date,
            lead.modified_date,
        ),
        screening_result: lead.screening_result ?? 'pending',
        background_check: lead.background_check ?? 'pending',
        credit_score: lead.credit_score ?? undefined,
        screening_status: lead.screening_request?.status,
        screening_invitation_url:
            lead.screening_request?.invitation_url || undefined,
        screening_report_url: lead.screening_report_url || undefined,
        screening_candidate_id:
            lead.screening_request?.checkr_candidate_id || undefined,
        can_start_screening: canStartScreening({
            pipelineStage: lead.pipeline_stage,
            screeningStatus: lead.screening_request?.status,
            screeningResult: lead.screening_result,
        }),
        can_resend_screening: canResendScreeningInvitation({
            pipelineStage: lead.pipeline_stage,
            screeningStatus: lead.screening_request?.status,
            invitationUrl: lead.screening_request?.invitation_url,
            candidateId: lead.screening_request?.checkr_candidate_id,
        }),
        can_send_screening_link: canSendScreeningLink({
            pipelineStage: lead.pipeline_stage,
            applicationStatus: lead.application_status,
            pendingApplicationReview:
                lead.pipeline_stage === 'app_signed_pending_review' ||
                Boolean(signing?.pending_review),
            screeningStatus: lead.screening_request?.status,
        }),
        lease_packet_preview: lead.lease_packet_preview,
        can_send_lease_package: canSendLeasePackage({
            screeningResult: lead.screening_result,
        }),
        tour_status: lead.current_tour?.status,
        tour_date: lead.current_tour?.starts_at ?? undefined,
        tour_staff: lead.current_tour?.given_by?.name,
        tour_id: lead.current_tour?.id,
        pets: lead.pets ?? [],
    }
}
